import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import './AccountHeadModal.css';

const BalanceSheetHeadModal = ({
  open,
  onClose,
  modalAction,
  selectedItem,
  formData,
  setFormData,
  onSave,
  loading
}) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedItem && modalAction !== 'create') {
      setFormData(selectedItem);
    } else if (modalAction === 'create') {
      setFormData(getDefaultFormData());
    }
  }, [selectedItem, modalAction, setFormData]);

  const getDefaultFormData = () => {
    return {
      type: 'balanceSheetHead',
      Name: '',
      Description: '',
      IsActive: true
    };
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Helper to get value from either lowercase or PascalCase field
    const getValue = (lowercase, pascalCase) => formData[lowercase] ?? formData[pascalCase];
    
    if (!(getValue('name', 'Name')?.toString().trim())) {
      newErrors.name = 'Balance Sheet Head name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave();
    }
  };

  const getModalTitle = () => {
    const actionNames = {
      create: 'New',
      edit: 'Edit',
      view: 'View'
    };
    
    const actionName = actionNames[modalAction] || 'New';
    
    return {
      title: `${actionName} Balance Sheet Head`,
      subtitle: `${modalAction === 'create' ? 'Add a new' : modalAction === 'edit' ? 'Update' : 'View'} balance sheet head ${modalAction === 'create' ? 'to your system' : 'details'}`
    };
  };

  const renderForm = () => (
    <div className="form-container">
      {/* Basic Information Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 1 }}>
        Basic Information
      </Typography>
      
      <div className="form-row">
        <div className="form-field required-field">
          <TextField
            fullWidth
            label="Balance Sheet Head Name *"
            placeholder="Enter balance sheet head name"
            value={formData.Name || formData.name || ''}
            onChange={(e) => handleInputChange('Name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            disabled={modalAction === 'view'}
            required
          />
        </div>
        <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsActive ?? formData.isActive ?? true}
                onChange={(e) => handleInputChange('IsActive', e.target.checked)}
                disabled={modalAction === 'view'}
              />
            }
            label="Active"
          />
        </div>
      </div>

      {/* Description Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 2 }}>
        Additional Details
      </Typography>
      
      <div className="form-field">
        <TextField
          fullWidth
          label="Description"
          placeholder="Enter description (optional, max 150 chars)"
          multiline
          rows={2}
          value={formData.Description || formData.description || ''}
          onChange={(e) => handleInputChange('Description', e.target.value)}
          disabled={modalAction === 'view'}
          inputProps={{ maxLength: 150 }}
          helperText={`${(formData.Description || formData.description || '').length}/150 characters`}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      className="account-head-modal"
    >
      <DialogTitle>
        <div className="modal-title-container">
          <div className="modal-title-text">
            <h2>{getModalTitle().title}</h2>
          </div>
          <Button
            onClick={onClose}
            className="modal-close-btn"
          >
            <Close />
          </Button>
        </div>
      </DialogTitle>
      
      <DialogContent>
        {renderForm()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {modalAction !== 'view' && (
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {modalAction === 'create' ? 'Add Balance Sheet Head' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BalanceSheetHeadModal;